import React, { useState, useEffect, useContext } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AuthContext } from "../../shared/context/auth-context";
import { ClinicaContext } from "../../shared/context/clinica-context";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { useLoading } from "../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import "./ListaEspera.scss";

import Navbar from "../../shared/components/navbar/Navbar";
import Sidebar from "../../shared/components/sidebar/Sidebar";

import { CalendarClock } from "lucide-react";

import ClientAutocomplete from "./components/ClienteAutocomplete";

// Componente para cada item da lista (draggable)
const SortableItem = ({
  item,
  onEdit,
  onUpdateStatus,
  onDelete,
  onSchedule,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "urgente":
        return "priority-urgent";
      case "normal":
        return "priority-normal";
      case "rotina":
        return "priority-routine";
      default:
        return "";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "ativo":
        return <span className="badge badge-active">Ativo</span>;
      case "contactado":
        return <span className="badge badge-contacted">Contactado</span>;
      case "agendado":
        return <span className="badge badge-scheduled">Agendado</span>;
      case "desistiu":
        return <span className="badge badge-cancelled">Desistiu</span>;
      default:
        return null;
    }
  };

  const getDaysWaiting = (dateEntry) => {
    const days = Math.floor(
      (new Date() - new Date(dateEntry)) / (1000 * 60 * 60 * 24),
    );
    return days;
  };

  const getAvailabilityText = (availability) => {
    switch (availability) {
      case "qualquer":
        return "Qualquer horário";
      case "manha":
        return "Apenas manhã";
      case "tarde":
        return "Apenas tarde";
      default:
        return availability;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`waiting-list-item ${getPriorityClass(item.prioridade)}`}
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        <svg width="20" height="20" viewBox="0 0 20 20">
          <path
            d="M7 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"
            fill="currentColor"
          />
        </svg>
      </div>

      <div className="item-content">
        <div className="item-header">
          <div className="patient-info">
            <h4>
              {item.pacienteProvisorio
                ? item.pacienteProvisorio.nome
                : item.cliente?.nome}
            </h4>
            <span className="contact">
              {item.pacienteProvisorio
                ? item.pacienteProvisorio.contacto
                : item.cliente?.contacto}
            </span>
          </div>
          {getStatusBadge(item.status)}
        </div>

        <div className="item-details">
          <div className="detail-row">
            <span className="label">Médico:</span>
            <span className="value">
              {item.medico?.name || "Qualquer médico"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Procedimento:</span>
            <span className="value">
              {item.categoriaTratamento?.categoria ||
                item.tratamento?.designacao}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Disponibilidade:</span>
            <span className="value">
              {getAvailabilityText(item.disponibilidade)}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Espera:</span>
            <span className="value days-waiting">
              {getDaysWaiting(item.dataEntrada)} dias
            </span>
          </div>
        </div>

        {item.observacoes && (
          <div className="observations">
            <span className="label">Obs:</span> {item.observacoes}
          </div>
        )}
      </div>

      <div className="item-actions">
        {/* <button
          className="btn-action btn-schedule"
          onClick={() => onSchedule(item)}
          disabled={item.status === "agendado"}
        >
          Agendar
        </button> */}
        <button className="btn-action btn-edit" onClick={() => onEdit(item)}>
          Editar
        </button>
        <select
          className="status-select"
          value={item.status}
          onChange={(e) => onUpdateStatus(item._id, e.target.value)}
        >
          <option value="ativo">Ativo</option>
          <option value="contactado">Contactado</option>
          <option value="agendado">Agendado</option>
          <option value="desistiu">Desistiu</option>
        </select>
        <button
          className="btn-action btn-delete"
          onClick={() => onDelete(item._id)}
        >
          Remover
        </button>
      </div>
    </div>
  );
};

// Modal para adicionar/editar entrada
const WaitingListModal = ({
  isOpen,
  onClose,
  onSave,
  entry,
  medicos,
  categorias,
  tratamentos,
  clientes,
  tipoAgendamento,
}) => {
  const [formData, setFormData] = useState({
    tipoMedico: entry?.medico ? "especifico" : "qualquer",
    medico: entry?.medico?._id || "",
    tipoPaciente: entry?.pacienteProvisorio ? "provisorio" : "existente",
    cliente: entry?.cliente?._id || "",
    pacienteProvisorio: {
      nome: entry?.pacienteProvisorio?.nome || "",
      contacto: entry?.pacienteProvisorio?.contacto || "",
      genero: entry?.pacienteProvisorio?.genero || "feminino",
    },
    tipoProcedimento: tipoAgendamento,
    categoriaTratamento: entry?.categoriaTratamento?._id || "",
    tratamento: entry?.tratamento?._id || "",
    prioridade: entry?.prioridade || "normal",
    disponibilidade: entry?.disponibilidade || "qualquer",
    diasEspecificos: entry?.diasEspecificos || [],
    observacoes: entry?.observacoes || "",
  });

  useEffect(() => {
    if (entry) {
      setFormData({
        tipoMedico: entry.medico ? "especifico" : "qualquer",
        medico: entry.medico?._id || "",
        tipoPaciente: entry.cliente?._id ? "existente" : "provisorio",
        cliente: entry.cliente?._id || "",
        pacienteProvisorio: {
          nome: entry.pacienteProvisorio?.nome || "",
          contacto: entry.pacienteProvisorio?.contacto || "",
          genero: entry.pacienteProvisorio?.genero || "feminino",
        },
        tipoProcedimento: tipoAgendamento,
        categoriaTratamento: entry.categoriaTratamento?._id || "",
        tratamento: entry.tratamento?._id || "",
        prioridade: entry.prioridade || "normal",
        disponibilidade: entry.disponibilidade || "qualquer",
        diasEspecificos: entry.diasEspecificos || [],
        observacoes: entry.observacoes || "",
      });
    }
  }, [entry]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        tipoMedico: "qualquer",
        medico: "",
        tipoPaciente: "existente",
        cliente: "",
        pacienteProvisorio: { nome: "", contacto: "", genero: "feminino" },
        categoriaTratamento: "",
        tratamento: "",
        prioridade: "normal",
        disponibilidade: "qualquer",
        diasEspecificos: [],
        observacoes: "",
      });
    }
  }, [isOpen]);

  const handleSubmit = () => {
    // Validações
    if (formData.tipoMedico === "especifico" && !formData.medico) {
      toast.error("Por favor selecione um médico");
      return;
    }

    if (formData.tipoPaciente === "existente" && !formData.cliente) {
      toast.error("Por favor selecione um paciente");
      return;
    }

    if (formData.tipoPaciente === "provisorio") {
      if (
        !formData.pacienteProvisorio.nome ||
        !formData.pacienteProvisorio.contacto
      ) {
        toast.error("Por favor preencha nome e contacto do paciente");
        return;
      }
    }

    if (tipoAgendamento === "Tratamento" && !formData.tratamento) {
      toast.error("Por favor selecione um tratamento");
      return;
    }

    if (tipoAgendamento !== "Tratamento" && !formData.categoriaTratamento) {
      toast.error("Por favor selecione uma categoria");
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{entry ? "Editar" : "Nova"} Entrada na Lista de Espera</h2>

        {/* Tipo de Médico */}
        <div className="form-group">
          <label>Médico</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="qualquer"
                checked={formData.tipoMedico === "qualquer"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tipoMedico: e.target.value,
                    medico: "",
                  })
                }
              />
              Qualquer médico
            </label>
            <label>
              <input
                type="radio"
                value="especifico"
                checked={formData.tipoMedico === "especifico"}
                onChange={(e) =>
                  setFormData({ ...formData, tipoMedico: e.target.value })
                }
              />
              Médico específico
            </label>
          </div>
          {formData.tipoMedico === "especifico" && (
            <select
              value={formData.medico}
              onChange={(e) =>
                setFormData({ ...formData, medico: e.target.value })
              }
            >
              <option value="">Selecione um médico</option>
              {medicos.map((medico) => (
                <option key={medico.id} value={medico.id}>
                  {medico.nome}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Tipo de Paciente */}
        <div className="form-group">
          <label>Paciente</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="existente"
                checked={formData.tipoPaciente === "existente"}
                onChange={(e) =>
                  setFormData({ ...formData, tipoPaciente: e.target.value })
                }
              />
              Paciente existente
            </label>
            <label>
              <input
                type="radio"
                value="provisorio"
                checked={formData.tipoPaciente === "provisorio"}
                onChange={(e) =>
                  setFormData({ ...formData, tipoPaciente: e.target.value })
                }
              />
              Paciente provisório
            </label>
          </div>

          {formData.tipoPaciente === "existente" ? (
            clientes && (
              <ClientAutocomplete
                selectedClient={formData.cliente}
                clients={clientes}
                onChange={(cli) => {
                  setFormData({ ...formData, cliente: cli?.id ? cli.id : "" });
                }}
              />
            )
          ) : (
            <div className="provisional-patient">
              <input
                type="text"
                placeholder="Nome*"
                value={formData.pacienteProvisorio.nome}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    pacienteProvisorio: {
                      ...formData.pacienteProvisorio,
                      nome: e.target.value,
                    },
                  })
                }
              />
              <input
                type="text"
                placeholder="Contacto*"
                value={formData.pacienteProvisorio.contacto}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    pacienteProvisorio: {
                      ...formData.pacienteProvisorio,
                      contacto: e.target.value,
                    },
                  })
                }
              />
              <select
                value={formData.pacienteProvisorio.genero}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    pacienteProvisorio: {
                      ...formData.pacienteProvisorio,
                      genero: e.target.value,
                    },
                  })
                }
              >
                <option value="masculino">Masculino</option>
                <option value="feminino">Feminino</option>
                <option value="outro">Outro</option>
              </select>
            </div>
          )}
        </div>

        {/* Tipo de Procedimento */}
        <div className="form-group">
          <label>Procedimento</label>
          {tipoAgendamento === "Tratamento" ? (
            <select
              value={formData.tratamento}
              onChange={(e) =>
                setFormData({ ...formData, tratamento: e.target.value })
              }
            >
              <option value="">Selecione um tratamento</option>
              {tratamentos.map((trat) => (
                <option key={trat.id} value={trat.id}>
                  {trat.label}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={formData.categoriaTratamento}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  categoriaTratamento: e.target.value,
                })
              }
            >
              <option value="">Selecione uma categoria</option>
              {categorias.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Prioridade */}
        <div className="form-group">
          <label>Prioridade</label>
          <select
            value={formData.prioridade}
            onChange={(e) =>
              setFormData({ ...formData, prioridade: e.target.value })
            }
          >
            <option value="urgente">Urgente</option>
            <option value="normal">Normal</option>
            <option value="rotina">Rotina</option>
          </select>
        </div>

        {/* Disponibilidade */}
        <div className="form-group">
          <label>Disponibilidade</label>
          <select
            value={formData.disponibilidade}
            onChange={(e) =>
              setFormData({ ...formData, disponibilidade: e.target.value })
            }
          >
            <option value="qualquer">Qualquer horário</option>
            <option value="manha">Apenas manhã (até 13h)</option>
            <option value="tarde">Apenas tarde (após 13h)</option>
            {/* <option value="especifico">Dias específicos</option> */}
          </select>

          {/* {formData.disponibilidade === "especifico" && (
            <div className="weekdays-selector">
              {["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"].map(
                (dia, index) => (
                  <label key={index}>
                    <input
                      type="checkbox"
                      checked={formData.diasEspecificos.includes(index + 1)}
                      onChange={(e) => {
                        const dias = e.target.checked
                          ? [...formData.diasEspecificos, index + 1]
                          : formData.diasEspecificos.filter(
                              (d) => d !== index + 1,
                            );
                        setFormData({ ...formData, diasEspecificos: dias });
                      }}
                    />
                    {dia}
                  </label>
                ),
              )}
            </div>
          )} */}
        </div>

        {/* Observações */}
        <div className="form-group">
          <label>Observações</label>
          <textarea
            value={formData.observacoes}
            onChange={(e) =>
              setFormData({ ...formData, observacoes: e.target.value })
            }
            rows="3"
          />
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onClose} className="btn-cancel">
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} className="btn-save">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente principal
const ListaEspera = () => {
  const { startLoading, stopLoading } = useLoading();

  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);

  const [waitingList, setWaitingList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tratamentos, setTratamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    medico: "todos",
    status: "todos",
    prioridade: "todos",
    procedimento: "todos",
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...waitingList];

    if (filters.medico !== "todos") {
      filtered = filtered.filter((item) =>
        filters.medico === "qualquer"
          ? !item.medico
          : item.medico?._id === filters.medico,
      );
    }

    if (filters.status !== "todos") {
      filtered = filtered.filter((item) => item.status === filters.status);
    }

    if (filters.prioridade !== "todos") {
      filtered = filtered.filter(
        (item) => item.prioridade === filters.prioridade,
      );
    }

    if (filters.procedimento !== "todos") {
      filtered = filtered.filter(
        (item) =>
          item.categoriaTratamento?._id === filters.procedimento ||
          item.tratamento?._id === filters.procedimento,
      );
    }

    setFilteredList(filtered);
  }, [waitingList, filters]);

  const fetchData = async () => {
    startLoading();
    try {
      const [
        responseListaEspera,
        responseClientes,
        responseTratamentos,
        responseCategoriasTratamento,
      ] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/nomeid/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/agendatratamento/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/categoriastratamento/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        ),
      ]);

      setWaitingList(responseListaEspera.listaEspera);
      setCategorias(
        responseCategoriasTratamento.categoriasTratamento.map((c) => ({
          id: c._id,
          label: c.categoria,
        })),
      );
      setTratamentos(
        responseTratamentos.tratamentos.map((t) => ({
          id: t._id,
          label: t.fornecedor?.nome
            ? `${t.designacao} (${t.fornecedor.nome})`
            : t.designacao,
        })),
      );
      setClientes(
        responseClientes.clientes.map((c) => ({
          id: c._id,
          label: c.nome,
        })),
      );
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar dados");
    } finally {
      stopLoading();
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = filteredList.findIndex((item) => item._id === active.id);
      const newIndex = filteredList.findIndex((item) => item._id === over.id);

      const newFilteredArray = arrayMove(filteredList, oldIndex, newIndex);

      // Reconstruir a lista completa mantendo a nova ordem dos itens filtrados
      const newFullArray = [...waitingList];
      newFilteredArray.forEach((filteredItem, newIdx) => {
        const fullIdx = newFullArray.findIndex(
          (i) => i._id === filteredItem._id,
        );
        newFullArray[fullIdx] = { ...newFullArray[fullIdx], ordem: newIdx };
      });
      newFullArray.sort((a, b) => a.ordem - b.ordem);

      setFilteredList(newFilteredArray);
      setWaitingList(newFullArray);

      try {
        const formData = new FormData();
        formData.append(
          "listaIds",
          JSON.stringify(newFullArray.map((item) => item._id)),
        );
        formData.append("clinicaId", clinica.clinica._id);
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/reordenarListaEspera/${auth.userId}`,
          "PATCH",
          formData,
          {
            Authorization: "Bearer " + auth.token,
          },
        );
        toast.success("Ordem atualizada");
      } catch (err) {
        toast.error("Erro ao reordenar");
      }
    }
  };

  const handleSaveEntry = async (formData) => {
    startLoading();
    try {
      const formDataEnviar = new FormData();
      const tipoAgendamento =
        clinica.clinica.configuracoes.configuracoes.tipoAgendamento.tipo;
      formDataEnviar.append("clinica", clinica.clinica._id);
      formDataEnviar.append("tipoMedico", formData.tipoMedico);
      formDataEnviar.append("medico", formData.medico);
      formDataEnviar.append("tipoPaciente", formData.tipoPaciente);
      formDataEnviar.append("cliente", formData.cliente);
      formDataEnviar.append(
        "pacienteProvisorio",
        JSON.stringify(formData.pacienteProvisorio),
      );
      formDataEnviar.append(
        "tipoProcedimento",
        tipoAgendamento === "Tratamento" ? "Tratamento" : "Categoria",
      );
      formDataEnviar.append(
        "categoriaTratamento",
        formData.categoriaTratamento,
      );
      formDataEnviar.append("tratamento", formData.tratamento);
      formDataEnviar.append("prioridade", formData.prioridade);
      formDataEnviar.append("disponibilidade", formData.disponibilidade);
      formDataEnviar.append("diasEspecificos", formData.diasEspecificos);
      formDataEnviar.append("observacoes", formData.observacoes);
      formDataEnviar.append("utilizador", auth.userId);

      if (editingEntry) {
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/${editingEntry._id}`,
          "PATCH",
          formDataEnviar,
          { Authorization: "Bearer " + auth.token },
        );
      } else {
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/${auth.userId}`,
          "POST",
          formDataEnviar,
          { Authorization: "Bearer " + auth.token },
        );
      }

      toast.success(
        editingEntry
          ? "Entrada atualizada com sucesso"
          : "Entrada adicionada com sucesso",
      );
      setIsModalOpen(false);
      setEditingEntry(null);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao guardar entrada");
    } finally {
      stopLoading();
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const formData = new FormData();

      formData.append("status", newStatus);
      formData.append("utilizador", auth.userId);
      startLoading();
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/updateStatusListaEspera/${id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );
      setWaitingList((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, status: newStatus } : item,
        ),
      );

      toast.success("Status atualizado");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar status");
    } finally {
      stopLoading();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover esta entrada?")) return;

    try {
      startLoading();
      const formData = new FormData();
      formData.append("utilizador", auth.userId);
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/listaespera/deleteEntradaListaEspera/${id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      setWaitingList((prev) => prev.filter((item) => item._id !== id));
      toast.success("Entrada removida!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover entrada");
    } finally {
      stopLoading();
    }
  };

  const handleSchedule = (item) => {
    // TODO: Redirect to scheduling page with pre-filled data
    toast.info("Redirecionando para agendamento...");
  };

  return (
    <div className="list">
      <Sidebar />
      <div className="listContainer">
        <Navbar title="Lista de Espera" icon={CalendarClock} />
        <div className="waiting-list-page">
          <div className="page-header">
            <h1>Lista de Espera</h1>
            <button
              className="btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              + Nova Entrada
            </button>
          </div>

          <div className="filters-section">
            <select
              value={filters.medico}
              onChange={(e) =>
                setFilters({ ...filters, medico: e.target.value })
              }
            >
              <option value="todos">Todos os médicos</option>
              <option value="qualquer">Qualquer médico</option>
              {auth.usuariosAssociados.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <option value="todos">Todos os status</option>
              <option value="ativo">Ativo</option>
              <option value="contactado">Contactado</option>
              <option value="agendado">Agendado</option>
              <option value="desistiu">Desistiu</option>
            </select>

            <select
              value={filters.prioridade}
              onChange={(e) =>
                setFilters({ ...filters, prioridade: e.target.value })
              }
            >
              <option value="todos">Todas as prioridades</option>
              <option value="urgente">Urgente</option>
              <option value="normal">Normal</option>
              <option value="rotina">Rotina</option>
            </select>

            <select
              value={filters.procedimento}
              onChange={(e) =>
                setFilters({ ...filters, procedimento: e.target.value })
              }
            >
              <option value="todos">Todos os procedimentos</option>
              <optgroup label="Categorias">
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Tratamentos">
                {tratamentos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="list-container">
            {filteredList.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma entrada na lista de espera</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredList.map((item) => item._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredList.map((item) => (
                    <SortableItem
                      key={item._id}
                      item={item}
                      onEdit={(item) => {
                        setEditingEntry(item);
                        setIsModalOpen(true);
                      }}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                      onSchedule={handleSchedule}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

          <WaitingListModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingEntry(null);
            }}
            onSave={handleSaveEntry}
            entry={editingEntry}
            medicos={auth.usuariosAssociados}
            categorias={categorias}
            tratamentos={tratamentos}
            clientes={clientes}
            tipoAgendamento={
              clinica.clinica.configuracoes.configuracoes.tipoAgendamento.tipo
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ListaEspera;
